import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Copy, Share2, Store, Sparkles, Instagram, Facebook, Twitter,
  MessageCircle, Phone, CheckCircle2, AlertTriangle, Gift, X, ExternalLink,
} from 'lucide-react';
import CustomerAccountLayout from '../../components/CustomerAccountLayout';

/* ─────────────────────────────────────────────────────────────────────
 * Customer Account → Referrals (rebuilt Feb 2026 for the Gift Card
 * Credit system, share-action fraud fix June 2026). Two distinct invite
 * tracks + social share grid + weekly cap banner. Point values are read
 * live from the rewards config endpoint so the backend remains the
 * single source of truth.
 *
 * SHARE FRAUD FIX (iter81, June 2026): previously clicking any share
 * button immediately POSTed to /customers/share and awarded +2 pts
 * WITHOUT any real share happening — users could farm points by
 * spamming the buttons. The new flow:
 *
 *   1. Click → launch the actual platform share action (intent URL,
 *      navigator.share, or copy-to-clipboard + confirmation modal for
 *      Instagram/TikTok which have no web share endpoint).
 *   2. Only after the share action LAUNCHED successfully → POST
 *      /customers/share to award points. If the popup is blocked, the
 *      user cancels the share sheet, or they dismiss the confirmation
 *      modal, NO points are awarded.
 *
 * NEW point economy (1 pt = $0.50):
 *   • Friend signup        → +1 pt   ($0.50)
 *   • Restaurant referral  → +60 pts ($30.00) — fires only after the
 *                            restaurant completes onboarding AND its
 *                            FIRST subscription payment lands.
 *   • Social share         → +2 pts  ($1.00) — capped at 15 rewarded
 *                            shares per rolling 7-day window
 *                            (= 30 pts = $15 weekly max).
 * ─────────────────────────────────────────────────────────────────── */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHARE_PROVIDERS = [
  { key: 'instagram', label: 'Instagram',   Icon: Instagram,      tone: 'text-pink-300/85' },
  { key: 'tiktok',    label: 'TikTok',      Icon: Sparkles,       tone: 'text-cyan-200/85' },
  { key: 'facebook',  label: 'Facebook',    Icon: Facebook,       tone: 'text-blue-300/85' },
  { key: 'twitter',   label: 'X / Twitter', Icon: Twitter,        tone: 'text-white/85' },
  { key: 'whatsapp',  label: 'WhatsApp',    Icon: MessageCircle,  tone: 'text-emerald-300/85' },
  { key: 'sms',       label: 'SMS',         Icon: Phone,          tone: 'text-yellow-300/85' },
];

// Pre-typed share message — keep short so it fits inside the URL-length
// limits Twitter/SMS impose, and leaves room for the inviter's link.
const SHARE_TEXT = 'Order direct from your favorite local restaurants on NoFee — 0% commission, better prices, free Gift Card Credits.';

// Platforms that expose a real web share intent. We open these in a new
// tab; points fire ONLY if window.open returns a non-null window
// reference (popup blockers + invalid URLs return null).
const SHARE_URL_BUILDERS = {
  facebook: (url) =>
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  twitter: (url) =>
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(url)}`,
  whatsapp: (url) =>
    `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${url}`)}`,
  sms: (url) =>
    `sms:?&body=${encodeURIComponent(`${SHARE_TEXT} ${url}`)}`,
};

// Instagram + TikTok have NO public web share URL — the platforms ship a
// share intent only inside their native apps. We try the Web Share API
// first (mobile browsers + recent desktop Safari), then fall back to a
// copy-link confirmation modal which forces the user to actually paste
// the link into the app before claiming the reward.
const CLIPBOARD_ONLY_PROVIDERS = new Set(['instagram', 'tiktok']);

const AccountReferrals = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_customer_token') : null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tracks rewarded shares this rolling week + cap (returned by the
  // backend on every successful POST /customers/share). Hydrated lazily
  // — undefined means "not yet known", which keeps the banner hidden
  // until we have a real count.
  const [shareUsage, setShareUsage] = useState({ rewarded_this_week: null, limit: null });
  // Active confirmation modal for Instagram/TikTok — null when closed.
  // { network, label } when the user has just copied the link and needs
  // to confirm they posted before we credit the reward.
  const [confirmShare, setConfirmShare] = useState(null);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/customers/me/rewards`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        // Pre-fill the weekly counter from the rewards summary so the
        // banner shows the correct state on page load (before any share).
        const cfg = r.data?.config || {};
        const rewardedThisWeek = r.data?.summary?.rewarded_shares_this_week ?? null;
        setShareUsage({
          rewarded_this_week: rewardedThisWeek,
          limit: cfg.max_rewarded_shares_per_week ?? 15,
        });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const cfg = data?.config || {};
  const code = data?.summary?.referral_code || '';
  const friendLink = code ? `${window.location.origin}/ref/friend/${code.toLowerCase()}` : '';
  const restaurantLink = code ? `${window.location.origin}/ref/restaurant/${code.toLowerCase()}` : '';
  const friends = data?.friends_invited || [];
  const restRefs = data?.restaurant_referrals || [];

  const pointsPerShare = cfg.points_per_social_share ?? 2;
  const pointsPerFriend = cfg.points_per_referral_signup ?? 1;
  const pointsPerRestaurant = cfg.points_per_restaurant_referral_approved ?? 60;
  const weeklyLimit = shareUsage.limit ?? cfg.max_rewarded_shares_per_week ?? 15;
  const rewardedThisWeek = shareUsage.rewarded_this_week ?? 0;
  const capReached = rewardedThisWeek >= weeklyLimit;

  const copyLink = async (url, label) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`${label} link copied`);
    } catch {
      toast.error('Could not copy');
    }
  };

  // ─── Reward credit step — called ONLY after a real share action
  // (intent URL opened OR navigator.share resolved OR user explicitly
  // confirmed the post via the Instagram/TikTok modal). ───
  const creditShare = async (network) => {
    try {
      const res = await axios.post(
        `${API}/customers/share`,
        { network, url: friendLink },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.awarded) {
        toast.success(`+${res.data.points} pts · ${network}`);
        setShareUsage({
          rewarded_this_week: res.data.rewarded_this_week,
          limit: res.data.limit,
        });
      } else if (res.data?.reason === 'weekly_limit') {
        toast.message('Weekly Sharing Limit Reached', {
          description: `You have reached the maximum of ${res.data.limit} rewarded shares for this week.`,
        });
        setShareUsage({ rewarded_this_week: res.data.rewarded_this_week, limit: res.data.limit });
      } else {
        toast.message('Already shared on this network today.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message) || detail || 'Could not record share';
      toast.error(msg);
    }
  };

  // ─── Launch real share action — points are ONLY credited after the
  // share UI was actually opened or the user explicitly confirmed they
  // posted the link. No more "free" points on a bare button click. ───
  const handleShareClick = async (network, label) => {
    if (!friendLink) {
      toast.error('Your referral link is not ready yet.');
      return;
    }
    if (capReached) return; // button is also disabled — defensive

    // Step 1: try the Web Share API for ALL networks first — when
    // available (mobile browsers + Safari) it pops the OS share sheet,
    // which is the most honest share signal we can get.
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: 'NoFee',
          text: SHARE_TEXT,
          url: friendLink,
        });
        // Promise resolved => the user actually picked a target +
        // dispatched the share. Credit the reward.
        await creditShare(network);
        return;
      } catch (err) {
        // AbortError = user cancelled → NO reward (spec). Any other
        // error → fall through to the URL-intent fallback below.
        if (err?.name === 'AbortError') return;
      }
    }

    // Step 2 (desktop): platforms with a real web share URL.
    if (SHARE_URL_BUILDERS[network]) {
      const url = SHARE_URL_BUILDERS[network](friendLink);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        toast.error('Your browser blocked the share popup. Please allow popups and try again.');
        return;
      }
      // Window opened successfully = share dialog launched. Credit.
      await creditShare(network);
      return;
    }

    // Step 3 (desktop, Instagram/TikTok): no web share URL exists.
    // Copy the link and show an explicit "I posted — claim reward"
    // confirmation modal. Points fire ONLY when the user clicks the
    // confirm button inside the modal (i.e. self-attests that they
    // actually shared the link).
    if (CLIPBOARD_ONLY_PROVIDERS.has(network)) {
      try {
        await navigator.clipboard.writeText(friendLink);
      } catch {
        toast.error('Could not copy link. Please copy it manually first.');
      }
      setConfirmShare({ network, label });
      return;
    }

    // Defensive fallback — unknown network. Should never hit in
    // practice (every entry in SHARE_PROVIDERS has a builder OR is in
    // CLIPBOARD_ONLY_PROVIDERS).
    toast.error('This share network is not configured.');
  };

  // ─── Instagram/TikTok confirmation modal helpers ───
  const externalAppHomes = {
    instagram: 'https://www.instagram.com/',
    tiktok: 'https://www.tiktok.com/',
  };
  const confirmAndCredit = async () => {
    if (!confirmShare) return;
    const network = confirmShare.network;
    setConfirmShare(null);
    await creditShare(network);
  };

  return (
    <CustomerAccountLayout
      currentKey="referrals"
      title="Referrals"
      subtitle="Invite friends and restaurants. Earn Gift Card Credits."
    >
      {loading ? (
        <div className="text-white/45 text-[14px]" data-testid="referrals-loading">Loading…</div>
      ) : (
        <>
          {/* ───────── Friend referral link ───────── */}
          <div
            data-testid="referral-friend-link-card"
            className="p-5 sm:p-6 rounded-3xl border border-yellow-500/30 bg-gradient-to-br from-yellow-500/[0.06] via-zinc-950 to-black"
          >
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-yellow-300" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
                  Friend referral
                </span>
              </div>
              <span className="text-[11px] font-bold text-yellow-300/90 tabular-nums">
                +{pointsPerFriend} pt = ${(pointsPerFriend * 0.5).toFixed(2)}
              </span>
            </div>
            <p className="text-[13px] text-white/65 mb-3">
              Invite a friend. You earn when they create an account.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <code
                data-testid="referral-friend-link-text"
                className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-[12.5px] sm:text-[13px] text-yellow-200 font-mono break-all flex-1 min-w-0"
              >
                {friendLink || '—'}
              </code>
              <button
                type="button"
                onClick={() => copyLink(friendLink, 'Friend')}
                disabled={!friendLink}
                data-testid="referral-friend-link-copy"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[13px] disabled:opacity-50"
              >
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
          </div>

          {/* ───────── Restaurant referral link ───────── */}
          <div
            data-testid="referral-restaurant-link-card"
            className="mt-4 p-5 sm:p-6 rounded-3xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.06] via-zinc-950 to-black"
          >
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Store className="w-4 h-4 text-emerald-300" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-bold">
                  Restaurant referral
                </span>
              </div>
              <span className="text-[11px] font-bold text-emerald-300/95 tabular-nums">
                +{pointsPerRestaurant} pts = ${(pointsPerRestaurant * 0.5).toFixed(2)}
              </span>
            </div>
            <p className="text-[13px] text-white/65 mb-3">
              Bring a restaurant to NoFee. Points unlock once they complete onboarding and their first subscription payment lands.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <code
                data-testid="referral-restaurant-link-text"
                className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-[12.5px] sm:text-[13px] text-emerald-200 font-mono break-all flex-1 min-w-0"
              >
                {restaurantLink || '—'}
              </code>
              <button
                type="button"
                onClick={() => copyLink(restaurantLink, 'Restaurant')}
                disabled={!restaurantLink}
                data-testid="referral-restaurant-link-copy"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-b from-emerald-300 to-emerald-500 text-black font-extrabold text-[13px] disabled:opacity-50"
              >
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
          </div>

          {/* ───────── Stats ───────── */}
          <div className="mt-5 grid grid-cols-3 gap-3" data-testid="referral-stats">
            {[
              { Icon: Users, label: 'Friends invited',     value: friends.length, sub: `${friends.filter((f) => f.first_order_recorded).length} active` },
              { Icon: Store, label: 'Restaurants',         value: restRefs.length, sub: 'referrals tracked' },
              { Icon: Gift,  label: 'Pts this week',       value: rewardedThisWeek * pointsPerShare, sub: `${rewardedThisWeek}/${weeklyLimit} shares` },
            ].map((s) => (
              <div key={s.label} data-testid={`referral-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`} className="p-4 sm:p-5 rounded-2xl border border-white/8 bg-white/[0.02]">
                <s.Icon className="w-4 h-4 text-yellow-300/85 mb-2" />
                <div className="text-[10.5px] uppercase tracking-[0.2em] text-white/55 font-bold">{s.label}</div>
                <div className="mt-1 text-2xl sm:text-3xl font-extrabold text-white tabular-nums">{s.value}</div>
                <div className="mt-0.5 text-[11.5px] text-white/45">{s.sub}</div>
              </div>
            ))}
          </div>

          {/* ───────── Weekly cap banner ───────── */}
          {capReached && (
            <div
              data-testid="referral-weekly-limit-banner"
              className="mt-5 p-4 rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] flex gap-3 items-start"
              role="alert"
            >
              <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
              <div className="text-[13px] text-amber-100/90 leading-relaxed">
                <div className="font-bold text-amber-200 mb-1">Weekly Sharing Limit Reached</div>
                You have reached the maximum of {weeklyLimit} rewarded shares for this week. Additional shares will not earn points until next week.
              </div>
            </div>
          )}

          {/* ───────── Share & earn grid ───────── */}
          <div className="mt-7" data-testid="referral-share-block">
            <h2 className="text-[11px] uppercase tracking-[0.22em] text-yellow-300 font-bold mb-3 flex items-center gap-1.5">
              <Share2 className="w-3.5 h-3.5" /> Share & earn
            </h2>
            <p className="text-[12.5px] text-white/55 mb-3 leading-relaxed">
              Points unlock <strong className="text-white/75">after</strong> you actually post or send your referral link. Clicking a button alone won&apos;t earn rewards.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
              {SHARE_PROVIDERS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleShareClick(key, label)}
                  disabled={!friendLink || capReached}
                  data-testid={`referral-share-${key}`}
                  className="group p-3.5 rounded-2xl border border-white/8 bg-white/[0.02] hover:bg-white/[0.04] hover:border-yellow-500/30 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center group-hover:border-yellow-500/30 transition-colors">
                    <Icon className="w-4 h-4 text-yellow-300/85" />
                  </div>
                  <div className="mt-2.5 text-[12.5px] font-semibold text-white">{label}</div>
                  <div className="text-[10.5px] font-bold text-yellow-300/90 tabular-nums">
                    +{pointsPerShare} pts after share
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* ───────── Friends list ───────── */}
          {friends.length > 0 && (
            <div className="mt-7" data-testid="referral-friends-list">
              <h2 className="text-[11px] uppercase tracking-[0.22em] text-white/55 font-bold mb-3">Friends invited</h2>
              <ul className="divide-y divide-white/[0.05] rounded-2xl border border-white/8 bg-white/[0.015] overflow-hidden">
                {friends.slice(0, 25).map((f) => (
                  <li key={f.id} className="p-3.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-semibold text-white truncate">{f.name || f.email}</div>
                      <div className="text-[11.5px] text-white/45 truncate">{f.email}</div>
                    </div>
                    {f.first_order_recorded ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-emerald-500/30 bg-emerald-500/15 text-emerald-300 text-[10.5px] font-bold uppercase tracking-wider">
                        <CheckCircle2 className="w-3 h-3" /> Active
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-md border border-amber-500/30 bg-amber-500/15 text-amber-300 text-[10.5px] font-bold uppercase tracking-wider">
                        Pending
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ───────── Instagram/TikTok "did you post?" confirmation
              modal — the ONLY way to claim the +2 pts on these two
              platforms (since they have no web share URL). User must
              explicitly attest that they posted the copied link. ───── */}
          {confirmShare && (
            <div
              data-testid="share-confirm-modal"
              className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
              onClick={(e) => { if (e.target === e.currentTarget) setConfirmShare(null); }}
            >
              <div className="w-full max-w-md bg-zinc-950 border border-yellow-500/25 rounded-3xl shadow-2xl my-auto">
                <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
                      Share on {confirmShare.label}
                    </div>
                    <h2 className="text-lg font-extrabold text-white mt-0.5">Confirm your share</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmShare(null)}
                    data-testid="share-confirm-close"
                    className="p-1.5 rounded-full hover:bg-white/5"
                  >
                    <X className="w-4 h-4 text-white/70" />
                  </button>
                </div>

                <div className="px-5 py-4 space-y-3.5">
                  <p className="text-[13.5px] text-white/75 leading-relaxed">
                    {confirmShare.label} doesn&apos;t support direct web sharing, so we&apos;ve copied your referral link to your clipboard.
                  </p>
                  <ol className="space-y-2 text-[13px] text-white/75 list-decimal pl-5">
                    <li>Open <strong className="text-yellow-300">{confirmShare.label}</strong>.</li>
                    <li>Paste the link into a new post, story or message.</li>
                    <li>When you&apos;ve posted it, come back and tap <strong>I posted it</strong> below.</li>
                  </ol>

                  <div className="flex items-center gap-2 flex-wrap">
                    <code
                      data-testid="share-confirm-link"
                      className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-[12.5px] text-yellow-200 font-mono break-all flex-1 min-w-0"
                    >
                      {friendLink}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyLink(friendLink, 'Referral')}
                      data-testid="share-confirm-copy"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/15 text-white/85 text-[12.5px] font-bold hover:bg-white/5"
                    >
                      <Copy className="w-3.5 h-3.5" /> Copy again
                    </button>
                  </div>

                  <a
                    href={externalAppHomes[confirmShare.network] || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="share-confirm-open-app"
                    className="inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 rounded-xl border border-white/15 text-white/85 text-[13px] font-bold hover:bg-white/5"
                  >
                    Open {confirmShare.label} <ExternalLink className="w-3.5 h-3.5" />
                  </a>

                  <p className="text-[11.5px] text-white/45 leading-relaxed">
                    Tapping <strong className="text-white/65">I posted it</strong> confirms you actually shared the link. Empty confirmations may be reviewed and reversed by our team.
                  </p>
                </div>

                <div className="px-5 py-4 border-t border-white/10 flex flex-col sm:flex-row gap-2.5">
                  <button
                    type="button"
                    onClick={() => setConfirmShare(null)}
                    data-testid="share-confirm-cancel"
                    className="flex-1 px-4 py-3 rounded-xl border border-white/15 text-white/80 font-semibold text-[13.5px] hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmAndCredit}
                    data-testid="share-confirm-submit"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[13.5px]"
                  >
                    <CheckCircle2 className="w-4 h-4" /> I posted it — claim +{pointsPerShare} pts
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </CustomerAccountLayout>
  );
};

export default AccountReferrals;
